import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const API_BASE_URL = 'https://nesolearn-backend.onrender.com';

const SUGGESTED_PROMPTS = [
  'Explain this concept',
  'Give me an example',
  'Test my understanding',
  'What should I review before this?',
];

const NesoGuideChat = ({ courseTitle, lessonTitle, lessonExcerpt, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, sending]);

  const sendMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;

    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setInput('');
    setSending(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/neso-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          context: { courseTitle, lessonTitle, lessonExcerpt },
        }),
      });

      if (!res.ok) throw new Error('request failed');
      const data = await res.json();
      setMessages((prev) => [...prev, { role: 'assistant', text: data?.reply || "I couldn't quite get that — try rephrasing?" }]);
    } catch (err) {
      setMessages((prev) => [...prev, { role: 'assistant', text: 'Neso Guide is temporarily unavailable. Please try again in a moment.', isError: true }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white/70 backdrop-blur-xl border border-white/40 rounded-2xl shadow-sm flex flex-col h-full">
      <div className="px-4 py-3.5 border-b border-border/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Sparkles" size={14} className="text-accent" />
          </div>
          <span className="text-sm font-semibold text-foreground">Neso Guide</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="lg:hidden p-1 rounded hover:bg-muted">
            <Icon name="X" size={16} className="text-muted-foreground" />
          </button>
        )}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-3.5 py-3 space-y-3 min-h-0">
        {messages.length === 0 && (
          <div className="text-center pt-6 px-2">
            <p className="text-xs text-muted-foreground mb-4">
              Ask about this lesson, get an example, or test yourself.
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {SUGGESTED_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => sendMessage(p)}
                  className="text-xs px-2.5 py-1.5 rounded-full bg-muted/60 hover:bg-muted text-foreground transition-smooth"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
                m.role === 'user'
                  ? 'bg-accent text-white rounded-br-sm'
                  : m.isError
                  ? 'bg-destructive/10 text-destructive rounded-bl-sm'
                  : 'bg-muted text-foreground rounded-bl-sm'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}

        {sending && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex gap-1">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce"
                  style={{ animationDelay: `${i * 120}ms` }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-3.5 py-3 border-t border-border/60 flex items-end gap-2">
        <textarea
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage(input);
            }
          }}
          placeholder="Ask Neso Guide…"
          className="flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring max-h-24"
        />
        <Button variant="default" size="icon" onClick={() => sendMessage(input)} disabled={!input.trim() || sending}>
          <Icon name="ArrowUp" size={16} />
        </Button>
      </div>
    </div>
  );
};

export default NesoGuideChat;